import type { ReactElement } from "react";
import { ListGroupItem } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { UnpackResponse } from "../../../api/src/lib";
import type { InsightsController } from "../../../api/src/insights/insights.controller";
import { useAuthenticatedFetch } from "../lib/api";
import Loading from "../Loading";
import InsightFailed from "./InsightFailed";

const TradeProposalSummary = () => {
  const {
    isPending: isPendingTradeProposalSummary,
    data: dataTradeProposalSummary,
  } = useAuthenticatedFetch<
    UnpackResponse<InsightsController["tradeProposalSummary"]>
  >("/insights/trade-proposal-summary");
  const summary = dataTradeProposalSummary?.data;

  if (isPendingTradeProposalSummary) {
    return (
      <ListGroupItem>
        <Loading />
      </ListGroupItem>
    );
  }

  if (!summary) {
    return <InsightFailed />;
  }

  const totalRaiseCash = summary.raiseCash + summary.spendCash;
  const parts: ReactElement[] = [];

  // APP-1279: deep-link each entry point into the list pre-filtered by type.
  if (summary.tolerance > 0) {
    parts.push(
      <Link
        key="tolerance"
        to={`/rebalances/today?q=${encodeURIComponent('type = "tolerance"')}`}
      >
        {summary.tolerance} Households with Tolerance
      </Link>,
    );
  }

  if (totalRaiseCash > 0) {
    parts.push(
      <Link
        key="cash"
        to={`/rebalances/today?q=${encodeURIComponent(
          'type = "raise_cash" or type = "spend_cash"',
        )}`}
      >
        {totalRaiseCash} Households with Raise Cash/Spend Cash
      </Link>,
    );
  }

  if (summary.taxLossHarvest > 0) {
    parts.push(
      <Link
        key="tlh"
        to={`/rebalances/today?q=${encodeURIComponent('type = "tlh"')}`}
      >
        {summary.taxLossHarvest} Households with Tax-Loss Harvest
      </Link>,
    );
  }

  if (parts.length === 0) {
    return (
      <ListGroupItem>
        Your Trade Proposal summary today is 0 Households.
      </ListGroupItem>
    );
  }

  return (
    <ListGroupItem>
      Your Trade Proposal summary today is{" "}
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 && index === parts.length - 1 && " and "}
          {index > 0 && index < parts.length - 1 && ", "}
          {part}
        </span>
      ))}
      .
    </ListGroupItem>
  );
};

export default TradeProposalSummary;
